/* GS://PROTOCOLO
 * Arquivo: src/pages/admin/UserManagementPage.js
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 3.8 – Integração Ajuste de Pontos (Wallet)
 * Impacto da Versão: [src/pages/admin/UserManagementPage.js]
 * Data: 29/12/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - [ADD]: Importação de AdjustPointsForm.
 * - [ADD]: Função handleAdjustPoints para abrir modal de pontos.
 * - [MODIFIED]: Repasse da prop onAdjustPoints para UserListTable.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../firebase/config';
import { getDoc, doc } from 'firebase/firestore';

import { listUsers } from '../../services/firestoreService';

import AddUserForm from '../../components/forms/AddUserForm';
import EditUserForm from '../../components/forms/EditUserForm';
import AdjustPointsForm from '../../components/forms/AdjustPointsForm'; // [GS-NEW]
import UserListTable from './UserListTable';

const UserManagementPage = ({ openModal, closeModal }) => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await listUsers();
      const usersFromAuth = result.users;

      const staffPromises = usersFromAuth.map(u => getDoc(doc(db, 'staff', u.uid)));
      const staffSnapshots = await Promise.all(staffPromises);

      const combinedUsers = usersFromAuth.map(user => {
        const staffDoc = staffSnapshots.find(snap => snap.id === user.uid);
        if (staffDoc && staffDoc.exists()) {
          const staffData = staffDoc.data();
          return { ...user, ...staffData };
        }
        return user;
      });

      const sortedUsers = combinedUsers.sort(
        (a, b) => new Date(b.creationTime) - new Date(a.creationTime)
      );

      setUsers(sortedUsers);
    } catch (err) {
      console.error("Erro detalhado ao buscar usuários:", err);
      const errorMessage = `Erro: ${err.message} (Código: ${err.code || 'sem_codigo'})`;
      setError(errorMessage);

      if (openModal) {
        openModal(
          <p className="text-red-500">Erro ao carregar usuários: {errorMessage}</p>,
          "Erro de Carregamento",
          "lg"
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [openModal]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleEditUser = (user) => {
    if (openModal && closeModal) {
      openModal(
        <EditUserForm
          userToEdit={user}
          onFormSubmit={handleUpdateSuccess}
          onCancel={closeModal}
        />,
        `Editar Usuário: ${user?.displayName || 'Sem Nome'}`,
        '2xl'
      );
    }
  };

  // [GS-NEW]: Handler para abrir o modal de pontos
  const handleAdjustPoints = (user) => {
    if (openModal && closeModal) {
      openModal(
        <AdjustPointsForm
          user={user}
          onClose={closeModal}
          onSuccess={() => {
            // Opcional: Recarregar lista se ela mostrasse o saldo
            // fetchUsers(); 
          }}
        />,
        `Ajuste de Pontos (Wallet): ${user?.displayName || 'Usuário'}`,
        'lg'
      );
    }
  };

  const handleUpdateSuccess = () => {
    if (closeModal) closeModal();
    fetchUsers();
  };

  return (
    <div className="container mx-auto px-4 py-6 sm:px-6 lg:px-8 bg-gray-50 text-gray-900 min-h-screen">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Gestão de Usuários</h1>
        <p className="text-gray-500 mt-2">
          Adicione, visualize e gerencie os usuários e suas permissões no sistema.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        <div className="lg:col-span-1">
          <AddUserForm onUserAdded={fetchUsers} />
        </div>

        <div className="lg:col-span-2">
          <UserListTable
            users={users}
            isLoading={isLoading}
            error={error}
            onEditUser={handleEditUser}
            onAdjustPoints={handleAdjustPoints} // [GS-NEW]: Passando a função
          />
        </div>
      </div>
    </div>
  );
};

export default UserManagementPage;